import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ActiveSubscription, findActiveSubscription, getActiveSubscriptions } from '../api/Subscription';
import { SUBSCRIPTION_REFETCH_INTERVAL_MS, SUBSCRIPTION_REFRESH_KEY } from '../constants/subscription';
import { AppContext } from '../context/AppContext';
import ensureError from '../utils/ensureError';
const SUBSCRIPTION_REFRESH_TTL_MS = 15 * 60 * 1000;

type UseActiveSubscriptionOptions = {
  poll?: boolean;
  tryLimit?: number;
  delayMs?: number;
};

export default function useActiveSubscription(options: UseActiveSubscriptionOptions = {}) {
  const { poll = false, tryLimit = 10, delayMs = 2500 } = options;

  const { activeSubscription, setActiveSubscription, subscriptionsDisabled } = useContext(AppContext) as {
    activeSubscription: ActiveSubscription | undefined;
    setActiveSubscription: (sub: ActiveSubscription | undefined) => void;
    subscriptionsDisabled: boolean;
  };

  const [fetchedSubscriptions, setFetchedSubscriptions] = useState(false);
  const [isPolling, setIsPolling] = useState<boolean>(poll);
  const [error, setError] = useState<Error | null>(null);

  const tries = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearPollTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const consumeRefreshRequest = (): boolean => {
    try {
      const raw = window.localStorage.getItem(SUBSCRIPTION_REFRESH_KEY);
      if (!raw) return false;

      // Clear regardless to keep it one-shot
      window.localStorage.removeItem(SUBSCRIPTION_REFRESH_KEY);

      const requestedAt = Number(raw);
      if (!Number.isFinite(requestedAt)) return false;

      return Date.now() - requestedAt <= SUBSCRIPTION_REFRESH_TTL_MS;
    } catch {
      return false;
    }
  };

  const fetchSubs = useCallback(async (): Promise<void> => {
    try {
      const res = await getActiveSubscriptions();

      if (res) {
        const found = res.active_subscriptions?.length ? findActiveSubscription(res.active_subscriptions) : undefined;
        setActiveSubscription(found);
      }
    } catch (err) {
      setError(ensureError(err));
    } finally {
      setFetchedSubscriptions(true);
    }
  }, [setActiveSubscription]);

  // Initial fetch: always fetch from API when subscriptions enabled.
  // Persisted activeSubscription is used for initial render; API is source of truth.
  useEffect(() => {
    if (subscriptionsDisabled) {
      setFetchedSubscriptions(true);
      return;
    }

    consumeRefreshRequest();
    void fetchSubs();
  }, [subscriptionsDisabled, fetchSubs]);

  // Periodic refetch when user has active subscription. Detects backend deletion (e.g. after grace period).
  useEffect(() => {
    if (subscriptionsDisabled || !activeSubscription) return;

    const intervalId = setInterval(() => {
      void fetchSubs();
    }, SUBSCRIPTION_REFETCH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [subscriptionsDisabled, activeSubscription, fetchSubs]);

  // Refetch when user returns to tab. Catches subscription deletion while tab was in background.
  useEffect(() => {
    if (subscriptionsDisabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchSubs();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [subscriptionsDisabled, fetchSubs]);

  // Optional polling (only if poll = true)
  useEffect(() => {
    if (!poll) return; // default behaviour: exit early
    if (subscriptionsDisabled) return setIsPolling(false);
    if (activeSubscription) return setIsPolling(false);

    const pollLoop = async () => {
      if (activeSubscription) {
        clearPollTimeout();
        setIsPolling(false);
        return;
      }

      if (tries.current >= tryLimit) {
        clearPollTimeout();
        setIsPolling(false);
        setError(new Error('Subscription did not become active in time.'));
        return;
      }

      tries.current += 1;
      await fetchSubs();

      timeoutRef.current = setTimeout(() => {
        void pollLoop();
      }, delayMs);
    };

    if (!subscriptionsDisabled) void pollLoop();

    return () => {
      clearPollTimeout();
    };
  }, [poll, activeSubscription, subscriptionsDisabled, tryLimit, delayMs, fetchSubs]);

  return {
    activeSubscription,
    fetchedSubscriptions,
    isPolling,
    error,
  };
}
